import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { MapMode } from '@warplan/shared';
import { api } from '../lib/api-client.js';
import { useAuth } from '../lib/auth-store.js';
import { NewWarButton } from './NewWarButton.js';
import { NewOperationButton } from './NewOperationButton.js';
import { PresenceModal } from './PresenceModal.js';
import { RegionSearch, type RegionSearchItem } from './RegionSearch.js';
import { SandboxActionsMenu } from './SandboxActionsMenu.js';
import { getMapFlavour, isSandbox, type ViewMode } from '../lib/view-mode.js';

export interface MapTopbarProps {
  onOpenHelp: () => void;
  presence?: number;
  foxholeUpdatedAt?: string | null;
  foxholeOffline?: boolean;
  searchItems?: ReadonlyArray<RegionSearchItem>;
  onSearchSelect?: (item: RegionSearchItem) => void;
  /** Current number of map objects (drives "New war" confirm copy). */
  assetCount?: number;
  /** Current number of operation-mode objects (drives "New operation" confirm copy). */
  operationObjectCount?: number;
  /** Current number of operation-mode arrows (drives "New operation" confirm copy). */
  operationArrowCount?: number;
  /** Active view mode (planner / operation / sandbox-*). */
  viewMode?: ViewMode;
  /** Switch view-mode handler. When omitted, the toggle is hidden. */
  onChangeViewMode?: (next: ViewMode) => void;
  /**
   * Remembered sandbox flavour the topbar's "Sandbox" button should re-enter
   * when toggled (defaults to `planner`). Updated by the parent each time
   * the user picks a sandbox flavour.
   */
  lastSandboxFlavour?: MapMode;
}

export function MapTopbar({
  onOpenHelp,
  presence = 0,
  foxholeUpdatedAt,
  foxholeOffline = false,
  searchItems,
  onSearchSelect,
  assetCount = 0,
  operationObjectCount = 0,
  operationArrowCount = 0,
  viewMode,
  onChangeViewMode,
  lastSandboxFlavour = 'planner',
}: MapTopbarProps): React.JSX.Element {
  const account = useAuth((s) => s.account);
  const logout = useAuth((s) => s.logout);
  const isAdmin = account?.role === 'admin';
  const [presenceOpen, setPresenceOpen] = useState(false);
  const sandboxActive = !!viewMode && isSandbox(viewMode);
  const sandboxFlavour: MapMode = viewMode ? getMapFlavour(viewMode) : 'planner';

  return (
    <header className="flex h-10 shrink-0 items-center gap-3 border-b border-slate-800 bg-slate-950 px-3 text-sm text-slate-200">
      <Link to="/" className="flex items-center gap-1.5 font-bold tracking-tight text-emerald-400 hover:text-emerald-300">
        <img src="/logo.png" alt="" className="w-6 h-6 object-contain" />
        Foxhole War-Plan
      </Link>

      {searchItems && onSearchSelect && (
        <RegionSearch items={searchItems} onSelect={onSearchSelect} />
      )}

      <div className="flex-1" />
      <VictoryPointsBadge />
      <div className="flex-1" />

      {viewMode && onChangeViewMode && (
        <div className="flex items-center gap-2">
          <div
            role="group"
            aria-label="Mode"
            className="flex items-center overflow-hidden rounded border border-slate-700"
          >
            <button
              type="button"
              onClick={() => onChangeViewMode('planner')}
              aria-pressed={viewMode === 'planner'}
              title="Mode Planner — préparation logistique et war-plan (synchronisé)"
              className={
                'px-2 py-0.5 text-xs transition ' +
                (viewMode === 'planner'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-900 text-slate-300 hover:bg-slate-800')
              }
            >
              Planner
            </button>
            <button
              type="button"
              onClick={() => onChangeViewMode('operation')}
              aria-pressed={viewMode === 'operation'}
              title="Mode Operation — préparation d'opération tactique (synchronisé)"
              className={
                'px-2 py-0.5 text-xs transition ' +
                (viewMode === 'operation'
                  ? 'bg-orange-600 text-white'
                  : 'bg-slate-900 text-slate-300 hover:bg-slate-800')
              }
            >
              Operation
            </button>
            <button
              type="button"
              onClick={() =>
                onChangeViewMode(
                  lastSandboxFlavour === 'operation' ? 'sandbox-operation' : 'sandbox-planner',
                )
              }
              aria-pressed={sandboxActive}
              title="Mode Sandbox — bac à sable local, non synchronisé"
              className={
                'px-2 py-0.5 text-xs transition ' +
                (sandboxActive
                  ? 'bg-amber-600 text-white'
                  : 'bg-slate-900 text-slate-300 hover:bg-slate-800')
              }
            >
              🧪 Sandbox
            </button>
          </div>

          {sandboxActive && (
            <div
              role="group"
              aria-label="Sandbox flavour"
              className="flex items-center overflow-hidden rounded border border-amber-700"
            >
              <button
                type="button"
                onClick={() => onChangeViewMode('sandbox-planner')}
                aria-pressed={viewMode === 'sandbox-planner'}
                title="Sandbox — saveur Planner (icônes & panneau planner)"
                className={
                  'px-2 py-0.5 text-xs transition ' +
                  (viewMode === 'sandbox-planner'
                    ? 'bg-emerald-700 text-white'
                    : 'bg-slate-900 text-slate-300 hover:bg-slate-800')
                }
              >
                Planner
              </button>
              <button
                type="button"
                onClick={() => onChangeViewMode('sandbox-operation')}
                aria-pressed={viewMode === 'sandbox-operation'}
                title="Sandbox — saveur Operation (icônes & panneau operation)"
                className={
                  'px-2 py-0.5 text-xs transition ' +
                  (viewMode === 'sandbox-operation'
                    ? 'bg-orange-700 text-white'
                    : 'bg-slate-900 text-slate-300 hover:bg-slate-800')
                }
              >
                Operation
              </button>
            </div>
          )}

          {sandboxActive && <SandboxActionsMenu flavour={sandboxFlavour} />}
        </div>
      )}

      {isAdmin && viewMode === 'planner' && <NewWarButton assetCount={assetCount} />}

      {isAdmin && viewMode === 'operation' && (
        <NewOperationButton
          objectCount={operationObjectCount}
          arrowCount={operationArrowCount}
        />
      )}

      {foxholeOffline ? (
        <span
          className="rounded bg-slate-800 px-2 py-0.5 text-xs text-red-400"
          title="Foxhole War API unreachable — icons may be outdated"
        >
          WarAPI: offline
        </span>
      ) : foxholeUpdatedAt ? (
        <span
          className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-400"
          title={`War data fetched at ${new Date(foxholeUpdatedAt).toLocaleString()}`}
        >
          WarAPI:{' '}
          {new Date(foxholeUpdatedAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ) : (
        <span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-500">WarAPI: …</span>
      )}

      {presence > 0 && (
        <button
          type="button"
          onClick={() => setPresenceOpen(true)}
          className="rounded bg-emerald-900/40 px-2 py-0.5 text-xs text-emerald-300 hover:bg-emerald-800/60 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          title="Show connected users"
          aria-haspopup="dialog"
        >
          {presence} online
        </button>
      )}

      <PresenceModal open={presenceOpen} onClose={() => setPresenceOpen(false)} />

      {isAdmin && (
        <Link to="/admin" className="rounded bg-amber-700 px-2 py-1 text-xs hover:bg-amber-600">
          Admin
        </Link>
      )}

      <span className="text-slate-400">{account?.pseudo}</span>
      <button
        type="button"
        onClick={onOpenHelp}
        className="rounded px-2 py-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
        title="Help"
      >
        ?
      </button>
      <button
        type="button"
        onClick={() => void logout()}
        className="rounded bg-slate-800 px-2 py-1 hover:bg-slate-700"
      >
        Logout
      </button>
    </header>
  );
}

// ---------------------------------------------------------------------------
// VictoryPointsBadge
// ---------------------------------------------------------------------------
//
// Compact badge showing the current Victory Town count per faction. A Victory
// Town is any dynamic map item with the `IsVictoryBase` flag set (0x01).
// Scorched victory towns (IsScorched = 0x10) are excluded since they no longer
// count toward the win condition. The total required to win is read from the
// /api/foxhole/war endpoint (`requiredVictoryTowns`, minus scorched count).
// Reference: https://github.com/clapfoot/warapi
//
// The component shares the React-Query cache (['foxhole','all-dynamic']) with
// MapPage, so the dynamic poll costs no extra network traffic.

const FLAG_IS_VICTORY_BASE = 0x01;
const FLAG_IS_SCORCHED = 0x10;

interface DynamicMapItem {
  iconType: number;
  teamId: string | null;
  flags: number;
}

interface DynamicAllResponse {
  fetchedAt: string;
  offline: boolean;
  maps: Array<{ mapName: string; mapItems: DynamicMapItem[] }>;
}

interface WarStateResponse {
  warId?: string;
  warNumber?: number;
  requiredVictoryTowns?: number;
}

interface Tally {
  colonial: number;
  warden: number;
  scorched: number;
}

function tallyVictoryTowns(data: DynamicAllResponse | undefined): Tally {
  let colonial = 0;
  let warden = 0;
  let scorched = 0;
  for (const map of data?.maps ?? []) {
    for (const item of map.mapItems) {
      if ((item.flags & FLAG_IS_VICTORY_BASE) !== FLAG_IS_VICTORY_BASE) continue;
      const isScorched = (item.flags & FLAG_IS_SCORCHED) === FLAG_IS_SCORCHED;
      if (isScorched) {
        scorched += 1;
        continue;
      }
      if (item.teamId === 'COLONIALS') colonial += 1;
      else if (item.teamId === 'WARDENS') warden += 1;
    }
  }
  return { colonial, warden, scorched };
}

/** Mini SVG flag tinted with a given faction colour. */
function FactionFlag({ color }: { color: string }): React.JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      className="shrink-0"
    >
      {/* pole */}
      <rect x="3" y="2" width="1.2" height="12" fill="#cbd5e1" />
      {/* flag cloth */}
      <path
        d="M4.2 2.5 L13 2.5 L11 5.5 L13 8.5 L4.2 8.5 Z"
        fill={color}
        stroke="#0b0d12"
        strokeWidth="0.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function VictoryPointsBadge(): React.JSX.Element | null {
  const dynamicQuery = useQuery({
    queryKey: ['foxhole', 'all-dynamic'],
    queryFn: () => api.get<DynamicAllResponse>('/api/foxhole/dynamic/all'),
    staleTime: 25_000,
    refetchInterval: 30_000,
  });
  const warQuery = useQuery({
    queryKey: ['foxhole', 'war'],
    queryFn: () => api.get<WarStateResponse>('/api/foxhole/war'),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  if (!dynamicQuery.data || dynamicQuery.data.offline) return null;

  const { colonial, warden, scorched } = tallyVictoryTowns(dynamicQuery.data);
  if (colonial === 0 && warden === 0) return null;

  // Per the WarAPI docs, the required count must be reduced by one for every
  // scorched victory town. Fallback to colonial+warden if /war is unavailable.
  const requiredRaw = warQuery.data?.requiredVictoryTowns;
  const required =
    typeof requiredRaw === 'number' ? Math.max(0, requiredRaw - scorched) : colonial + warden;

  const warNumber = warQuery.data?.warNumber;

  return (
    <div
      className="flex items-stretch overflow-hidden rounded border border-slate-700 bg-slate-900/80 text-xs text-slate-200 shadow-sm"
      title={
        warNumber !== undefined
          ? `War #${warNumber} — Victory Towns détenues / requises (hors scorched)`
          : 'Victory Towns détenues / requises (hors scorched)'
      }
    >
      <div className="flex items-center gap-1.5 px-2 py-0.5">
        <FactionFlag color="#516C4B" />
        <span className="font-mono">
          <span className="font-semibold text-emerald-300">{colonial}</span>
          <span className="px-0.5 text-slate-500">/</span>
          <span className="text-slate-400">{required}</span>
        </span>
      </div>
      <div className="w-px bg-slate-700" aria-hidden="true" />
      <div className="flex items-center gap-1.5 px-2 py-0.5">
        <FactionFlag color="#245682" />
        <span className="font-mono">
          <span className="font-semibold text-sky-300">{warden}</span>
          <span className="px-0.5 text-slate-500">/</span>
          <span className="text-slate-400">{required}</span>
        </span>
      </div>
    </div>
  );
}
